// src/services/googleAuth.ts
// Google Identity Services — uses the official renderButton API so the user
// clicks a real Google-rendered button.  This avoids the hidden-button hack
// and the deprecated One Tap prompt() that break under FedCM / CSP.

declare global {
  interface Window {
    google: any;
  }
}

export interface GoogleUser {
  email: string;
  first_name: string;
  last_name: string;
  google_id: string;
  avatar_url?: string;
}

type PendingResolve = (user: GoogleUser) => void;
type PendingReject = (err: Error) => void;

class GoogleAuthService {
  private isScriptLoaded = false;
  private isScriptLoading = false;
  private initialized = false;
  private clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Only one sign-in attempt at a time
  private pendingResolve: PendingResolve | null = null;
  private pendingReject: PendingReject | null = null;

  /** Load the GIS client script (idempotent). */
  private loadGoogleScript(): Promise<void> {
    if (this.isScriptLoaded && window.google?.accounts) {
      return Promise.resolve();
    }

    if (this.isScriptLoading) {
      return new Promise((resolve) => {
        const check = () =>
          window.google?.accounts ? resolve() : setTimeout(check, 100);
        check();
      });
    }

    this.isScriptLoading = true;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isScriptLoaded = true;
        this.isScriptLoading = false;
        // Small delay for the API to register on `window.google`
        setTimeout(() => {
          if (window.google?.accounts) {
            resolve();
          } else {
            reject(new Error('Google script loaded but API not available'));
          }
        }, 100);
      };

      script.onerror = () => {
        this.isScriptLoading = false;
        reject(new Error('Failed to load Google script'));
      };

      document.head.appendChild(script);
    });
  }

  /** Call initialize() at most once per page load. */
  private ensureInitialized(): void {
    if (this.initialized) return;
    if (!window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: any) => {
        if (!response?.credential) {
          this.pendingReject?.(new Error('No credential returned from Google'));
          this.pendingResolve = null;
          this.pendingReject = null;
          return;
        }
        try {
          const user = this.parseJwtToken(response.credential);
          this.pendingResolve?.(user);
        } catch (err) {
          this.pendingReject?.(err instanceof Error ? err : new Error(String(err)));
        }
        this.pendingResolve = null;
        this.pendingReject = null;
      },
      ux_mode: 'popup',          // explicit popup – no iframe needed
      use_fedcm_for_prompt: true, // opt-in to FedCM as Google recommends
    });

    this.initialized = true;
  }

  /**
   * Render the official Google Sign-In button inside `container`.
   * Returns a Promise that resolves once the user completes sign-in.
   */
  async renderButton(container: HTMLElement): Promise<GoogleUser> {
    await this.loadGoogleScript();

    if (!this.clientId) {
      throw new Error('Google Client ID not configured. Please check your environment variables.');
    }

    this.ensureInitialized();

    return new Promise<GoogleUser>((resolve, reject) => {
      // Store callbacks so the initialize() callback can settle the promise
      this.pendingResolve = resolve;
      this.pendingReject = reject;

      // Clear previous content and render the Google button
      container.innerHTML = '';
      window.google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: container.offsetWidth || 300,
      });
    });
  }

  /** Cancel any pending sign-in (e.g. on unmount). */
  cancel(): void {
    if (this.pendingReject) {
      this.pendingReject(new Error('Sign-in cancelled'));
      this.pendingResolve = null;
      this.pendingReject = null;
    }
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
  }

  private parseJwtToken(token: string): GoogleUser {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);

    return {
      email: payload.email,
      first_name: payload.given_name || '',
      last_name: payload.family_name || '',
      google_id: payload.sub,
      avatar_url: payload.picture,
    };
  }

  isAvailable(): boolean {
    return this.isScriptLoaded && !!window.google && !!this.clientId;
  }

  getClientId(): string | undefined {
    return this.clientId;
  }
}

export const googleAuthService = new GoogleAuthService();